import { AccessorialCode, ZonedDateTimeRange } from '@rygen/rygen-ui/model';
import { distinctBy } from '@rygen/rygen-ui/util';

import i18n from '@/i18n';

import { EmbeddableLocation } from '../EmbeddableLocation';
import {
  SHIPMENT_STOP_DROP_OFF_TYPES,
  SHIPMENT_STOP_PICKUP_TYPES,
  ShipmentStopType,
} from '../shipment/ShipmentStopType';
import { ShipOrderShippingPoint } from '../ShipOrderShippingPoint';
import { ShippingPoint } from '../ShippingPoint';

import { IConsolidatedShipmentStop } from './IConsolidatedShipmentStop';
import { ValidShipOrderShippingPoint } from './ValidShipOrderShippingPoint';

const TYPE = 'ConsolidatedShipment';

export type ConsolidatedShipmentStopOption = {
  sequenceNumber: number;
  shipOrderShippingPoints: ShipOrderShippingPoint[];
  requestedAppointmentRange?: ZonedDateTimeRange;
};

// prettier-ignore
export class ConsolidatedShipmentStop implements IConsolidatedShipmentStop, ShippingPoint {
  readonly shipOrderShippingPoints: ValidShipOrderShippingPoint[] = [];

  sequenceNumber: number;
  requestedAppointmentRange: ZonedDateTimeRange;

  constructor(opts: ConsolidatedShipmentStop | ConsolidatedShipmentStopOption) {
    this.sequenceNumber = opts.sequenceNumber;

    if (!opts.shipOrderShippingPoints.length) {
      throw new Error(
        'cannot create stop - at least one shipping point is required',
      );
    }
    opts.shipOrderShippingPoints.forEach((x) =>
      this.addShipOrderShippingPoint(x),
    );

    this.requestedAppointmentRange = new ZonedDateTimeRange({
      start: opts.requestedAppointmentRange?.start,
      end: opts.requestedAppointmentRange?.end,
    });
  }

  static isConsolidatedShipmentStop(
    location: ShippingPoint,
  ): location is ConsolidatedShipmentStop {
    return location.type === TYPE;
  }

  static copy(
    consolidatedShipmentStop: ConsolidatedShipmentStop,
  ): ConsolidatedShipmentStop {
    return new ConsolidatedShipmentStop(consolidatedShipmentStop);
  }

  get type() {
    return TYPE;
  }

  get requestedAppointmentEarliest() {
    return this.requestedAppointmentRange.start;
  }

  get requestedAppointmentLatest() {
    return this.requestedAppointmentRange.end;
  }

  get stopType(): ShipmentStopType {
    const stopType = this.shipOrderShippingPoints
      .map((x) => x.shippingPointType)
      .reduce((agg, curr, currIndex) => {
        const currShipmentStopType = ShipmentStopType[curr];
        if (currIndex === 0) {
          return currShipmentStopType;
        } else if (agg !== currShipmentStopType) {
          return ShipmentStopType.DROP_OFF_PICKUP;
        } else {
          return agg;
        }
      }, ShipmentStopType.UNKNOWN);
    return stopType;
  }

  get location(): EmbeddableLocation {
    return (
      this.shipOrderShippingPoints.find((x) => !!x)?.location ??
      new EmbeddableLocation()
    );
  }

  get requestedAccessorials(): AccessorialCode[] {
    return this.shipOrderShippingPoints
      .filter((x) => !!x)
      .flatMap((s) => s.requestedAccessorials)
      .filter(distinctBy('naturalId'));
  }

  get shipOrderShippingPointIds(): number[] {
    return this.shipOrderShippingPoints.map((x) => x.id);
  }

  /**
   * Useful when vue needs a :key for a list, but id or sequence number or the
   * index isn't appropriate
   * (e.g. in a sortablejs list)
   */
  get key(): string {
    return JSON.stringify(this);
  }

  get isInProgress() {
    return false;
  }

  get isPickup() {
    return (
      !!this.stopType && SHIPMENT_STOP_PICKUP_TYPES.includes(this.stopType)
    );
  }

  get isDropOff() {
    return (
      !!this.stopType && SHIPMENT_STOP_DROP_OFF_TYPES.includes(this.stopType)
    );
  }

  get stopLabel(): string {
    const stopName = [
      this.location.locationName,
      this.location.city,
      this.location.state,
    ]
      .filter((x) => !!x?.trim())
      .join(', ');

    const stopNumber = i18n.global.t('common.paramNum', {
      param: i18n.global.t('shipments.stops'),
      num: this.sequenceNumber,
    });

    return [stopNumber, stopName].join(' - ');
  }

  isResolved(_requireBothDates: boolean = false) {
    return false;
  }

  addShipOrderShippingPoint(
    shipOrderShippingPoint: ShipOrderShippingPoint,
  ): void {
    const validShipOrderShippingPoint = new ValidShipOrderShippingPoint(
      shipOrderShippingPoint,
    );
    this.shipOrderShippingPoints.push(validShipOrderShippingPoint);
  }

  toJSON(): IConsolidatedShipmentStop {
    return {
      sequenceNumber: this.sequenceNumber,
      stopType: this.stopType,
      shipOrderShippingPointIds: this.shipOrderShippingPointIds,
      requestedAppointmentRange: this.requestedAppointmentRange,
    };
  }
}
